import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { CircularProgress } from "@mui/material";
import { MAX_SIZE_MB } from "../../helper";

const customModalStyles = {
  content: {
    padding: "24px",
    width: "90%",
    maxWidth: "640px",
    margin: "auto",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

const isImageFile = (url = "") =>
  /\.(jpg|jpeg|png|webp|gif|bmp|svg)(\?|$)/i.test(String(url).split("?")[0] || "");

const FilePreview = ({ url, alt, style }) => {
  if (!url) return null;

  if (isImageFile(url)) {
    return (
      <img
        src={url}
        alt={alt || "Tender Document"}
        style={{ ...style, objectFit: "cover", borderRadius: "6px" }}
      />
    );
  }

  const label = /\.pdf(\?|$)/i.test(url) ? "PDF" : "DOC";

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...style,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f0f4f8",
        color: "#1e293b",
        textDecoration: "none",
        border: "1px solid #cbd5e1",
        borderRadius: "6px",
        padding: "10px",
        boxSizing: "border-box",
      }}
    >
      <span style={{ fontSize: 24, fontWeight: 700, color: "#2563eb" }}>{label}</span>
      <span style={{ fontSize: 12, marginTop: 4, color: "#475569" }}>Open / Download</span>
    </a>
  );
};

export default function Tender() {
  const [tenders, setTenders] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTenderId, setEditTenderId] = useState(null);
  const [tendersFetchLoading, setTendersFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [sortByLatest, setSortByLatest] = useState(true);
  const [docFile, setDocFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const initialFormData = {
    title: "",
    tenderNo: "",
    category: "Procurement",
    openingDate: "",
    closingDate: "",
    status: "Open",
    description: "",
    file: null,
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchTenders();
  }, []);

  const fetchTenders = async () => {
    setTendersFetchLoading(true);
    try {
      const response = await axiosInstance.get("tender/all");
      setTenders(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching tenders:", error);
    }
    setTendersFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > (MAX_SIZE_MB || 15)) {
        Swal.fire(
          "File Too Large",
          `File size exceeds ${MAX_SIZE_MB || 15} MB. Please select a smaller file.`,
          "warning"
        );
        e.target.value = "";
        return;
      }
      setDocFile(file);
      if (file.type.startsWith("image/")) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleCreateTender = async (e) => {
    e.preventDefault();
    setButtonLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "file") return;
      if (formData[key] !== null && formData[key] !== undefined) {
        formDataToSend.append(key, formData[key]);
      }
    });

    if (docFile) {
      formDataToSend.append("file", docFile);
    }

    try {
      await axiosInstance.post("tender/create", formDataToSend);
      fetchTenders();
      setShowCreateModal(false);
      setFormData(initialFormData);
      setDocFile(null);
      setFilePreview(null);
      Swal.fire("Success!", "Tender published successfully!", "success");
    } catch (error) {
      console.error("Error creating tender:", error);
      const msg = error.response?.data?.message || "Failed to create tender";
      Swal.fire("Error", msg, "error");
    }
    setButtonLoading(false);
  };

  const handleViewTender = async (id) => {
    try {
      setEditTenderId(id);
      setShowEditModal(true);
      const res = await axiosInstance.get(`tender/single/${id}`);
      const data = res?.data?.data;
      if (data) {
        setFormData({
          title: data.title || "",
          tenderNo: data.tenderNo || "",
          category: data.category || "Procurement",
          openingDate: data.openingDate || "",
          closingDate: data.closingDate || "",
          status: data.status || "Open",
          description: data.description || "",
          file: data.file || null,
        });
        if (data.file && isImageFile(data.file)) {
          setFilePreview(data.file);
        } else {
          setFilePreview(null);
        }
      }
    } catch (error) {
      console.error("Error fetching tender:", error);
      Swal.fire("Error", "Failed to fetch tender details.", "error");
    }
  };

  const handleEditTender = async (e) => {
    e.preventDefault();
    setButtonLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "file") return;
      if (formData[key] !== null && formData[key] !== undefined) {
        formDataToSend.append(key, formData[key]);
      }
    });

    if (docFile) {
      formDataToSend.append("file", docFile);
    }
    formDataToSend.append("_method", "PUT");

    try {
      await axiosInstance.post(`tender/change/${editTenderId}`, formDataToSend);
      fetchTenders();
      setShowEditModal(false);
      setDocFile(null);
      setFilePreview(null);
      Swal.fire("Success!", "Tender updated successfully!", "success");
    } catch (error) {
      console.error("Error updating tender:", error);
      const msg = error.response?.data?.message || "Failed to update tender";
      Swal.fire("Error", msg, "error");
    }
    setButtonLoading(false);
  };

  const handleDeleteTender = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Delete Tender?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await axiosInstance.delete(`tender/delete/${id}`);
        fetchTenders();
        Swal.fire("Deleted!", "Tender has been removed.", "success");
      }
    } catch (error) {
      console.error("Error deleting tender:", error);
      Swal.fire("Error", "Failed to delete tender", "error");
    }
  };

  const sortedTenders = sortByLatest
    ? [...tenders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : tenders;

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#1e293b" }}>
            Tenders Management
          </h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "14px" }}>
            Manage university procurement notices, RFPs, and tender documents
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => setSortByLatest((prev) => !prev)}
            style={{
              padding: "10px 16px",
              backgroundColor: sortByLatest ? "#3b82f6" : "#f1f5f9",
              color: sortByLatest ? "#fff" : "#334155",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {sortByLatest ? "Latest First" : "Default Order"}
          </button>
          <button
            onClick={() => {
              setFormData(initialFormData);
              setDocFile(null);
              setFilePreview(null);
              setShowCreateModal(true);
            }}
            style={{
              padding: "10px 20px",
              backgroundColor: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            + Add New Tender
          </button>
        </div>
      </div>

      {tendersFetchLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress />
        </div>
      ) : sortedTenders.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: "#fff",
            borderRadius: "8px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <p style={{ color: "#64748b", fontSize: "16px", margin: 0 }}>
            No tenders found. Click <strong>"+ Add New Tender"</strong> to create one.
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
          {sortedTenders.map((tender) => (
            <div
              key={tender._id}
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 8px",
                      backgroundColor: tender.status === "Open" ? "#dcfce7" : "#f1f5f9",
                      color: tender.status === "Open" ? "#15803d" : "#475569",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {tender.status || "Open"}
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      backgroundColor: "#f8fafc",
                      padding: "3px 8px",
                      borderRadius: "4px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    {tender.category || "Procurement"}
                  </span>
                </div>

                <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: "8px 0 4px" }}>
                  {tender.title}
                </h3>

                {tender.tenderNo && (
                  <p style={{ fontSize: "13px", color: "#2563eb", fontWeight: "600", margin: "2px 0 8px" }}>
                    Tender No: {tender.tenderNo}
                  </p>
                )}

                {tender.description && (
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#475569",
                      margin: "0 0 12px",
                      lineHeight: "1.4",
                      maxHeight: "60px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {tender.description}
                  </p>
                )}

                <div style={{ fontSize: "12px", color: "#64748b", display: "flex", flexDirection: "column", gap: "2px", marginBottom: "12px" }}>
                  {tender.openingDate && <div><strong>Opening:</strong> {tender.openingDate}</div>}
                  {tender.closingDate && <div><strong>Closing:</strong> {tender.closingDate}</div>}
                </div>

                {tender.file && (
                  <div style={{ marginBottom: "12px" }}>
                    <FilePreview url={tender.file} alt={tender.title} style={{ width: "100%", height: "100px" }} />
                  </div>
                )}
              </div>

              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "12px", marginTop: "12px" }}>
                <Actions
                  courseId={tender._id}
                  onEdit={handleViewTender}
                  onDelete={handleDeleteTender}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        style={customModalStyles}
        contentLabel="Create New Tender"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>Add New Tender</h2>
          <button
            onClick={() => setShowCreateModal(false)}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b" }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleCreateTender}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Tender Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g. Procurement of Lab Equipment for Department of IT"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Tender Number *
              </label>
              <input
                type="text"
                name="tenderNo"
                value={formData.tenderNo}
                onChange={handleChange}
                required
                placeholder="e.g. UO/TEN/2026/04"
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="Procurement">Procurement</option>
                <option value="Civil Works">Civil Works</option>
                <option value="Services">Services</option>
                <option value="Equipment">Equipment</option>
                <option value="IT & Networking">IT & Networking</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Opening Date
              </label>
              <input
                type="date"
                name="openingDate"
                value={formData.openingDate}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Closing Date
              </label>
              <input
                type="date"
                name="closingDate"
                value={formData.closingDate}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Awarded">Awarded</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Provide brief details, eligibility criteria, or instructions for bidders..."
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Upload Tender Document / PDF / Image
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
            {filePreview && (
              <img
                src={filePreview}
                alt="Preview"
                style={{ width: "120px", height: "auto", marginTop: "8px", borderRadius: "4px" }}
              />
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              style={{ padding: "10px 16px", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{ padding: "10px 24px", background: "#16a34a", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}
            >
              {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Publish Tender"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onRequestClose={() => setShowEditModal(false)}
        style={customModalStyles}
        contentLabel="Edit Tender"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>Edit Tender</h2>
          <button
            onClick={() => setShowEditModal(false)}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b" }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleEditTender}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Tender Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Tender Number *
              </label>
              <input
                type="text"
                name="tenderNo"
                value={formData.tenderNo}
                onChange={handleChange}
                required
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="Procurement">Procurement</option>
                <option value="Civil Works">Civil Works</option>
                <option value="Services">Services</option>
                <option value="Equipment">Equipment</option>
                <option value="IT & Networking">IT & Networking</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Opening Date
              </label>
              <input
                type="date"
                name="openingDate"
                value={formData.openingDate}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Closing Date
              </label>
              <input
                type="date"
                name="closingDate"
                value={formData.closingDate}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Awarded">Awarded</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Replace Tender Document / File (Optional)
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
            {formData.file && !docFile && (
              <div style={{ marginTop: "8px" }}>
                <p style={{ margin: "0 0 4px", fontSize: "12px", color: "#64748b" }}>Current Document:</p>
                <FilePreview url={formData.file} alt={formData.title} style={{ width: "100%", height: "80px" }} />
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              style={{ padding: "10px 16px", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{ padding: "10px 24px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}
            >
              {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
